const DEG_TO_RAD = Math.PI / 180

export interface CameraPositionConfig {
  targetSize: { width: number; depth: number }
  fov: number
  aspectRatio: number
  paddingFactor?: number
  topDownView?: boolean
  angleOffVerticalDegrees?: number
}

export interface CameraOffset {
  x: number
  y: number
  z: number
}

/**
 * Calculate camera distance needed to fit target in view.
 *
 * Math explanation:
 * - For a perspective camera with vertical FOV, at distance d:
 *   visible_height = 2 × d × tan(FOV/2)
 * - Rearranging: d = (visible_height / 2) / tan(FOV/2)
 * - We calculate distance needed for both width and depth dimensions
 * - Take the max to ensure both dimensions fit
 * - The angle parameter is NOT used here - viewing angle only affects
 *   camera position (x,y,z), not the distance needed to frame the object
 */
export function calculateCameraDistance(
  targetSize: { width: number; depth: number },
  fov: number,
  aspectRatio: number,
  paddingFactor: number = 1.5
): number {
  const fovRad = fov * DEG_TO_RAD

  const horizontalFov = 2 * Math.atan(Math.tan(fovRad / 2) * aspectRatio)

  const verticalDistance = targetSize.depth / 2 / Math.tan(fovRad / 2)
  const horizontalDistance = targetSize.width / 2 / Math.tan(horizontalFov / 2)

  const baseDistance = Math.max(verticalDistance, horizontalDistance)

  return baseDistance * paddingFactor
}

/**
 * Calculate camera offset (x, y, z) from target center.
 *
 * Math explanation:
 * - First calculates the distance needed to fit the object
 * - For top-down: camera is directly above at (0, distance, 0)
 * - For angled: camera is tilted by angleOffVertical from vertical axis
 *   - Height: distance × cos(angle)  [mostly vertical for small angles]
 *   - Horizontal: distance × sin(angle)  [slight offset for perspective]
 * - The angle only affects position, not the distance calculation
 */
export function calculateCameraOffset(
  config: CameraPositionConfig
): CameraOffset {
  const {
    targetSize,
    fov,
    aspectRatio,
    paddingFactor = 1.5,
    topDownView = false,
    angleOffVerticalDegrees = 15,
  } = config

  const minDistance = 30
  const distance = Math.max(
    minDistance,
    calculateCameraDistance(targetSize, fov, aspectRatio, paddingFactor)
  )

  if (topDownView) {
    return {
      x: 0,
      y: distance,
      z: 0,
    }
  }

  const angleOffVerticalRad = angleOffVerticalDegrees * DEG_TO_RAD
  const verticalDistance = distance * Math.cos(angleOffVerticalRad)
  const horizontalDistance = distance * Math.sin(angleOffVerticalRad)

  return {
    x: 0,
    y: verticalDistance,
    z: horizontalDistance,
  }
}

/**
 * Calculate orbit radius for spinning overview camera.
 *
 * Math explanation:
 * - Camera orbits around the model at a fixed radius
 * - Must see full bounding box from ALL rotation angles (0°, 90°, 180°, 270°)
 * - At 0°/180°: sees full width
 * - At 90°/270°: sees full depth
 * - At 45°/135°/etc: sees the diagonal (worst case)
 * - We use the diagonal as the "effective size" to ensure coverage at all angles
 * - Then calculate distance as normal for that size
 * - Note: height is ignored for mostly-flat roofs; could be enhanced for tall structures
 */
export function calculateOrbitRadius(
  boundingBoxDimensions: { width: number; depth: number; height: number },
  fov: number,
  aspectRatio: number,
  angleOffVerticalDegrees: number = 35,
  paddingFactor: number = 2.0
): number {
  const fovRad = fov * DEG_TO_RAD
  const horizontalFov = 2 * Math.atan(Math.tan(fovRad / 2) * aspectRatio)
  const angleOffVerticalRad = angleOffVerticalDegrees * DEG_TO_RAD

  // 1. Max horizontal size (floor diagonal)
  const diagonalSize = Math.sqrt(
    boundingBoxDimensions.width ** 2 + boundingBoxDimensions.depth ** 2
  )

  // 2. Max vertical size (projected 3D bounds)
  // The vertical extent on screen is the projection of the floor diagonal + the projection of the height
  const projectedVerticalSize =
    diagonalSize * Math.cos(angleOffVerticalRad) +
    boundingBoxDimensions.height * Math.sin(angleOffVerticalRad)

  // 3. Calculate distances needed
  const verticalDistance = projectedVerticalSize / 2 / Math.tan(fovRad / 2)
  const horizontalDistance = diagonalSize / 2 / Math.tan(horizontalFov / 2)

  const baseDistance = Math.max(verticalDistance, horizontalDistance)

  return baseDistance * paddingFactor
}
